import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Home, MapPin, Euro, Bed, Bath, Maximize, Image, ChevronRight, ChevronLeft, 
  Check, Crown, Search, CheckCircle, AlertCircle, Loader, Upload, X, GripVertical,
  Building, Calendar, TrendingUp, Percent, Calculator, FileText, Camera
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// LAS 52 PROVINCIAS OFICIALES DE ESPAÑA (50 + Ceuta y Melilla)
const SPAIN_PROVINCES = {
  // Andalucía (8)
  'Almería': { lat: 36.8340, lng: -2.4637 },
  'Cádiz': { lat: 36.5271, lng: -6.2886 },
  'Córdoba': { lat: 37.8882, lng: -4.7794 },
  'Granada': { lat: 37.1773, lng: -3.5986 },
  'Huelva': { lat: 37.2614, lng: -6.9447 },
  'Jaén': { lat: 37.7796, lng: -3.7849 },
  'Málaga': { lat: 36.7213, lng: -4.4214 },
  'Sevilla': { lat: 37.3891, lng: -5.9845 },
  // Aragón (3)
  'Huesca': { lat: 42.1401, lng: -0.4089 },
  'Teruel': { lat: 40.3456, lng: -1.1065 },
  'Zaragoza': { lat: 41.6488, lng: -0.8891 },
  // Asturias (1)
  'Asturias': { lat: 43.3614, lng: -5.8593 },
  // Islas Baleares (1)
  'Illes Balears': { lat: 39.5696, lng: 2.6502 },
  // Canarias (2)
  'Las Palmas': { lat: 28.1235, lng: -15.4363 },
  'Santa Cruz de Tenerife': { lat: 28.4636, lng: -16.2518 },
  // Cantabria (1)
  'Cantabria': { lat: 43.1828, lng: -3.9878 },
  // Castilla-La Mancha (5)
  'Albacete': { lat: 38.9943, lng: -1.8585 },
  'Ciudad Real': { lat: 38.9848, lng: -3.9274 },
  'Cuenca': { lat: 40.0704, lng: -2.1374 },
  'Guadalajara': { lat: 40.6337, lng: -3.1674 },
  'Toledo': { lat: 39.8628, lng: -4.0273 },
  // Castilla y León (9)
  'Ávila': { lat: 40.6565, lng: -4.6819 },
  'Burgos': { lat: 42.3439, lng: -3.6969 },
  'León': { lat: 42.5987, lng: -5.5671 },
  'Palencia': { lat: 42.0096, lng: -4.5288 },
  'Salamanca': { lat: 40.9688, lng: -5.6631 },
  'Segovia': { lat: 40.9429, lng: -4.1088 },
  'Soria': { lat: 41.7636, lng: -2.4649 },
  'Valladolid': { lat: 41.6523, lng: -4.7245 },
  'Zamora': { lat: 41.5034, lng: -5.7467 },
  // Cataluña (4)
  'Barcelona': { lat: 41.3851, lng: 2.1734 },
  'Girona': { lat: 41.9794, lng: 2.8214 },
  'Lleida': { lat: 41.6176, lng: 0.6200 },
  'Tarragona': { lat: 41.1189, lng: 1.2445 },
  // Comunidad Valenciana (3)
  'Alicante': { lat: 38.3452, lng: -0.4810 },
  'Castellón': { lat: 39.9864, lng: -0.0513 },
  'Valencia': { lat: 39.4699, lng: -0.3763 },
  // Extremadura (2)
  'Badajoz': { lat: 38.8794, lng: -6.9706 },
  'Cáceres': { lat: 39.4753, lng: -6.3724 },
  // Galicia (4)
  'A Coruña': { lat: 43.3623, lng: -8.4115 },
  'Lugo': { lat: 43.0097, lng: -7.5567 },
  'Ourense': { lat: 42.3364, lng: -7.8642 },
  'Pontevedra': { lat: 42.4298, lng: -8.6446 },
  // Madrid (1)
  'Madrid': { lat: 40.4168, lng: -3.7038 },
  // Murcia (1)
  'Murcia': { lat: 37.9922, lng: -1.1307 },
  // Navarra (1)
  'Navarra': { lat: 42.6954, lng: -1.6761 },
  // País Vasco (3)
  'Álava': { lat: 42.8467, lng: -2.6726 },
  'Bizkaia': { lat: 43.2630, lng: -2.9350 },
  'Gipuzkoa': { lat: 43.3183, lng: -1.9812 },
  // La Rioja (1)
  'La Rioja': { lat: 42.2871, lng: -2.5396 },
  // Ceuta y Melilla (2)
  'Ceuta': { lat: 35.8894, lng: -5.3198 },
  'Melilla': { lat: 35.2923, lng: -2.9381 },
};

const PROVINCE_LIST = Object.keys(SPAIN_PROVINCES).sort();

// Función para capitalizar correctamente las direcciones españolas
const capitalizeAddress = (address) => {
  if (!address) return '';
  
  // Palabras que deben ir en minúsculas (excepto al inicio)
  const lowercaseWords = ['de', 'del', 'la', 'el', 'los', 'las', 'y', 'a', 'e'];
  
  // Palabras clave que siempre se capitalizan
  const keywords = {
    'calle': 'Calle',
    'avenida': 'Avenida',
    'avda': 'Avda',
    'paseo': 'Paseo',
    'plaza': 'Plaza',
    'plza': 'Plza',
    'camino': 'Camino',
    'carretera': 'Carretera',
    'travesia': 'Travesía',
    'travesía': 'Travesía',
    'ronda': 'Ronda',
    'glorieta': 'Glorieta',
    'urbanizacion': 'Urbanización',
    'urbanización': 'Urbanización',
    'urb': 'Urb',
  };
  
  const words = address.split(' ');
  return words.map((word, index) => {
    if (!word) return word; // Skip empty strings
    const lowerWord = word.toLowerCase();
    
    // Si es palabra clave de dirección, usar la forma correcta
    if (keywords[lowerWord]) {
      return keywords[lowerWord];
    }
    
    // Palabras que van en minúsculas (excepto al inicio)
    if (lowercaseWords.includes(lowerWord) && index > 0) {
      return lowerWord;
    }
    
    // Para números o palabras con puntuación, mantener como están pero capitalizar primera letra
    if (/^\d+$/.test(word)) {
      return word; // Números se mantienen igual
    }
    
    // Capitalizar primera letra de cada palabra importante (nombres propios, etc.)
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');
};

// Categorías de imágenes
const IMAGE_CATEGORIES = [
  { id: 'portada', label: 'Portada', icon: '🏠' },
  { id: 'salon', label: 'Salón', icon: '🛋️' },
  { id: 'cocina', label: 'Cocina', icon: '🍳' },
  { id: 'dormitorio', label: 'Dormitorio', icon: '🛏️' },
  { id: 'bano', label: 'Baño', icon: '🚿' },
  { id: 'terraza', label: 'Terraza/Balcón', icon: '🌅' },
  { id: 'exterior', label: 'Exterior', icon: '🏡' },
  { id: 'piscina', label: 'Piscina', icon: '🏊' },
  { id: 'garaje', label: 'Garaje', icon: '🚗' },
  { id: 'jardin', label: 'Jardín', icon: '🌳' },
  { id: 'otros', label: 'Otros', icon: '📷' },
];

const PlusvaliaPreview = ({ purchasePrice, salePrice }) => {
  const purchase = parseFloat(purchasePrice);
  const price = parseFloat(salePrice);
  if (!purchase || !price || purchase <= 0 || price <= 0) return null;

  const gain = price - purchase;
  const gainPct = ((gain / purchase) * 100).toFixed(1);
  const landValue = price * 0.30;
  const plusvaliaEst = Math.round(landValue * 0.03 * 0.05 * 10);
  const irpf = gain > 0 ? Math.round(gain * 0.19) : 0;
  const neto = gain - irpf - plusvaliaEst;

  return (
    <div className="mt-3 bg-gray-700/50 rounded-xl p-4 border border-white/10 space-y-2">
      <p className="text-xs font-bold text-white/60 uppercase mb-2">Estimación en tiempo real</p>
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Ganancia bruta</span>
        <span className={`font-bold ${gain >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {gain >= 0 ? '+' : ''}{gain.toLocaleString('es-ES')} € ({gainPct}%)
        </span>
      </div>
      <div className="flex justify-between text-sm">
        <span className="text-gray-400">Plusvalía municipal est.</span>
        <span className="text-orange-400 font-bold">{plusvaliaEst.toLocaleString('es-ES')} €</span>
      </div>
      {gain > 0 && (
        <div className="flex justify-between text-sm">
          <span className="text-gray-400">IRPF estimado (19%)</span>
          <span className="text-red-400 font-bold">{irpf.toLocaleString('es-ES')} €</span>
        </div>
      )}
      <div className="flex justify-between text-sm border-t border-white/10 pt-2">
        <span className="text-gray-300 font-medium">Ganancia neta estimada</span>
        <span className={`font-black ${neto >= 0 ? 'text-green-400' : 'text-red-400'}`}>
          {neto.toLocaleString('es-ES')} €
        </span>
      </div>
    </div>
  );
};

export const Publish = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [addressSearching, setAddressSearching] = useState(false);
  const [addressVerified, setAddressVerified] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);
  const fileInputRef = useRef(null);
  const [draggedImage, setDraggedImage] = useState(null);
  
  const [formData, setFormData] = useState({
    // Datos básicos
    title: '',
    description: '',
    property_type: 'apartamento',
    listing_type: 'venta',
    price: '',
    purchase_price: '', // NUEVO: Precio de compra (para cálculos de plusvalía)
    // Características
    bedrooms: '',
    bathrooms: '',
    area: '',
    plot_area: '', // Área de parcela
    floor: '', // Planta
    total_floors: '', // Total plantas edificio
    orientations: [], // Array para múltiples orientaciones
    // Ubicación
    street_name: '',
    street_number: '',
    province: 'Madrid', // Provincia seleccionada
    city: '', // Localidad/Ciudad dentro de la provincia
    postal_code: '',
    coordinates: SPAIN_PROVINCES['Madrid'],
    verified_address: '',
    // Antigüedad y estado
    year_built: '',
    renovation_year: '',
    condition: 'buen_estado', // nuevo, buen_estado, reformar
    energy_cert: 'en_tramite', // OBLIGATORIO - valor por defecto
    // Extras
    has_elevator: false,
    has_parking: false,
    parking_included: false,
    parking_price: '',
    has_pool: false,
    has_garden: false,
    has_terrace: false,
    terrace_area: '',
    has_storage: false,
    has_ac: false,
    has_heating: false,
    heating_type: '',
    is_furnished: false,
    // Datos financieros (para calcular ROI, etc.)
    community_fee: '', // Gastos de comunidad mensual
    ibi_annual: '', // IBI anual
    purchase_price: '', // Precio de compra (si es diferente al de venta)
    rental_estimate: '', // Estimación alquiler mensual
    // Imágenes
    images: [] // Array de {file, preview, category, order}
  });

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleProvinceChange = (province) => {
    const coords = SPAIN_PROVINCES[province] || { lat: 40.4168, lng: -3.7038 };
    setFormData({...formData, province, coordinates: coords});
    setAddressVerified(false);
  };

  // Búsqueda de direcciones
  const searchAddress = useCallback(async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      return;
    }
    
    setAddressSearching(true);
    try {
      const searchQueries = [
        `${query}, ${formData.city}, España`,
        `${query}, España`,
      ];
      
      let allResults = [];
      
      for (const searchQuery of searchQueries) {
        try {
          const response = await axios.get(
            `https://nominatim.openstreetmap.org/search`,
            {
              params: {
                q: searchQuery,
                format: 'json',
                countrycodes: 'es',
                limit: 8,
                addressdetails: 1,
                'accept-language': 'es'
              },
              headers: { 'User-Agent': 'E-Home Real Estate App' }
            }
          );
          
          if (response.data?.length > 0) {
            allResults = [...allResults, ...response.data];
          }
        } catch (e) {
          console.log('Search failed:', searchQuery);
        }
      }
      
      const uniqueResults = allResults.reduce((acc, item) => {
        const key = `${item.lat}-${item.lon}`;
        if (!acc.find(r => `${r.lat}-${r.lon}` === key)) acc.push(item);
        return acc;
      }, []);
      
      if (uniqueResults.length > 0) {
        setAddressSuggestions(uniqueResults.slice(0, 6).map(item => ({
          display_name: item.display_name,
          lat: parseFloat(item.lat),
          lon: parseFloat(item.lon),
          address: item.address
        })));
        setShowSuggestions(true);
      } else {
        setAddressSuggestions([]);
      }
    } catch (error) {
      console.error('Error searching address:', error);
    } finally {
      setAddressSearching(false);
    }
  }, [formData.city]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.street_name && formData.street_name.length >= 3 && !addressVerified) {
        searchAddress(formData.street_name);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [formData.street_name, searchAddress, addressVerified]);

  const selectAddress = (suggestion) => {
    // Ajustar coordenadas ligeramente según el número de calle (aproximado)
    const streetNum = parseInt(formData.street_number) || 1;
    const offset = (streetNum / 200) * 0.001; // Pequeño offset basado en número
    
    setFormData({
      ...formData,
      verified_address: suggestion.display_name,
      coordinates: { 
        lat: suggestion.lat + (Math.random() * 0.0005 - 0.00025), // Ligera variación por privacidad
        lng: suggestion.lon + offset 
      }
    });
    setAddressVerified(true);
    setShowSuggestions(false);
    setAddressSuggestions([]);
    toast.success('¡Calle verificada! La ubicación en el mapa es aproximada por seguridad.');
  };

  const verifyCurrentAddress = async () => {
    if (!formData.street_name || formData.street_name.length < 3) {
      toast.error('Introduce el nombre de la calle');
      return;
    }
    
    setAddressSearching(true);
    try {
      const searchQuery = `${formData.street_name}, ${formData.city}, España`;
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search`,
        {
          params: {
            q: searchQuery,
            format: 'json',
            countrycodes: 'es',
            limit: 1,
            addressdetails: 1,
            'accept-language': 'es'
          },
          headers: { 'User-Agent': 'E-Home Real Estate App' }
        }
      );
      
      if (response.data?.length > 0) {
        const result = response.data[0];
        const streetNum = parseInt(formData.street_number) || 1;
        const offset = (streetNum / 200) * 0.001;
        
        setFormData({
          ...formData,
          verified_address: result.display_name,
          coordinates: { 
            lat: parseFloat(result.lat) + (Math.random() * 0.0005 - 0.00025),
            lng: parseFloat(result.lon) + offset 
          }
        });
        setAddressVerified(true);
        toast.success('¡Calle verificada! Ubicación aproximada por seguridad.');
      } else {
        // Usar coordenadas de la provincia
        const provinceCoords = SPAIN_PROVINCES[formData.province];
        setFormData({
          ...formData,
          verified_address: `${formData.street_name} ${formData.street_number}, ${formData.city}, ${formData.province}`,
          coordinates: provinceCoords
        });
        setAddressVerified(true);
        toast.success('Dirección guardada con ubicación de la ciudad');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al verificar');
    } finally {
      setAddressSearching(false);
    }
  };

  // === MANEJO DE IMÁGENES ===
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const newImages = files.map((file, idx) => ({
      id: `img_${Date.now()}_${idx}`,
      file,
      preview: URL.createObjectURL(file),
      category: formData.images.length === 0 && idx === 0 ? 'portada' : 'otros',
      order: formData.images.length + idx
    }));
    
    setFormData({...formData, images: [...formData.images, ...newImages]});
    toast.success(`${files.length} imagen(es) añadida(s)`);
  };

  const removeImage = (imageId) => {
    const updated = formData.images.filter(img => img.id !== imageId);
    // Reordenar
    updated.forEach((img, idx) => img.order = idx);
    setFormData({...formData, images: updated});
  };

  const updateImageCategory = (imageId, category) => {
    const updated = formData.images.map(img => 
      img.id === imageId ? {...img, category} : img
    );
    setFormData({...formData, images: updated});
  };

  const moveImage = (fromIndex, toIndex) => {
    const updated = [...formData.images];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    updated.forEach((img, idx) => img.order = idx);
    setFormData({...formData, images: updated});
  };

  const handleDragStart = (e, index) => {
    setDraggedImage(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedImage === null || draggedImage === index) return;
    moveImage(draggedImage, index);
    setDraggedImage(index);
  };

  const handleDragEnd = () => {
    setDraggedImage(null);
  };

  // Upload images to backend
  const uploadImages = async () => {
    if (formData.images.length === 0) return [];
    
    setUploadingImages(true);
    const uploadedUrls = [];
    
    try {
      for (const img of formData.images) {
        const formDataUpload = new FormData();
        formDataUpload.append('file', img.file);
        formDataUpload.append('category', img.category);
        
        const response = await axios.post(`${API}/upload/property-image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        uploadedUrls.push({
          url: response.data.url,
          category: img.category,
          order: img.order
        });
      }
      return uploadedUrls;
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Error al subir imágenes');
      return [];
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSubmit = async () => {
    if (!addressVerified) {
      toast.error('Por favor verifica la dirección antes de publicar');
      return;
    }
    
    if (formData.images.length === 0) {
      toast.error('Añade al menos una imagen de la propiedad');
      return;
    }
    
    setLoading(true);
    try {
      // Subir imágenes primero
      const uploadedImages = await uploadImages();
      
      if (uploadedImages.length === 0 && formData.images.length > 0) {
        throw new Error('Error al subir imágenes');
      }
      
      const fullAddress = capitalizeAddress(`${formData.street_name} ${formData.street_number}, ${formData.postal_code} ${formData.city}, ${formData.province}`);
      
      const submitData = {
        title: formData.title,
        description: formData.description,
        property_type: formData.property_type,
        listing_type: formData.listing_type,
        price: parseFloat(formData.price) || 0,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseInt(formData.bathrooms) || 0,
        area: parseFloat(formData.area) || 0,
        location: fullAddress,
        city: formData.city,
        province: formData.province,
        coordinates: formData.coordinates,
        images: uploadedImages,
        // Datos extendidos
        extended_data: {
          plot_area: parseFloat(formData.plot_area) || null,
          floor: parseInt(formData.floor) || null,
          total_floors: parseInt(formData.total_floors) || null,
          orientation: formData.orientations.length > 0 ? formData.orientations.join(', ') : null,
          year_built: parseInt(formData.year_built) || null,
          renovation_year: parseInt(formData.renovation_year) || null,
          condition: formData.condition,
          energy_cert: formData.energy_cert || null,
          postal_code: formData.postal_code,
          // Extras
          has_elevator: formData.has_elevator,
          has_parking: formData.has_parking,
          parking_included: formData.parking_included,
          parking_price: parseFloat(formData.parking_price) || null,
          has_pool: formData.has_pool,
          has_garden: formData.has_garden,
          has_terrace: formData.has_terrace,
          terrace_area: parseFloat(formData.terrace_area) || null,
          has_storage: formData.has_storage,
          has_ac: formData.has_ac,
          has_heating: formData.has_heating,
          heating_type: formData.heating_type || null,
          is_furnished: formData.is_furnished,
          // Financieros
          community_fee: parseFloat(formData.community_fee) || null,
          ibi_annual: parseFloat(formData.ibi_annual) || null,
          purchase_price: parseFloat(formData.purchase_price) || null,
          rental_estimate: parseFloat(formData.rental_estimate) || null,
        }
      };
      
      const response = await axios.post(`${API}/properties`, submitData);
      toast.success('¡Propiedad publicada con éxito!');
      navigate(`/my-properties`);
    } catch (error) {
      console.error('Error publishing property:', error);
      toast.error('Error al publicar la propiedad');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { num: 1, label: 'Básico' },
    { num: 2, label: 'Ubicación' },
    { num: 3, label: 'Características' },
    { num: 4, label: 'Extras' },
    { num: 5, label: 'Imágenes' },
    { num: 6, label: 'Confirmar' }
  ];

  return (
    <div className="min-h-screen bg-gray-950 p-4 md:p-8 lg:p-12">
      <div className="max-w-4xl mx-auto pt-20 md:pt-8">
        <h1 className={`text-4xl md:text-5xl font-bold mb-2 text-center md:text-left bg-gradient-to-r from-yellow-500 to-amber-500 bg-clip-text text-transparent pb-1`}>
          Publicar Propiedad
        </h1>
        <p className="text-gray-400 text-sm mb-6">Completa todos los datos para maximizar la visibilidad de tu anuncio</p>
        
        {/* Step indicator */}
        <div className="flex items-center gap-1 mb-8 overflow-x-auto pb-2">
          {steps.map((s, i) => (
            <React.Fragment key={s.num}>
              <button 
                onClick={() => s.num < step && setStep(s.num)}
                className={`flex items-center gap-1 ${step >= s.num ? 'text-white' : 'text-gray-600'} ${s.num < step ? 'cursor-pointer hover:opacity-80' : ''}`}
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                  step >= s.num ? `bg-gradient-to-r from-yellow-500 to-amber-500 text-black` : 'bg-gray-800 text-gray-500'
                }`}>
                  {step > s.num ? <Check size={12} /> : s.num}
                </div>
                <span className="text-xs hidden lg:inline whitespace-nowrap">{s.label}</span>
              </button>
              {i < steps.length - 1 && <div className={`flex-1 h-px min-w-[20px] ${step > s.num ? 'bg-white/30' : 'bg-gray-800'}`} />}
            </React.Fragment>
          ))}
        </div>
        
        <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 md:p-8">
          
          {/* === PASO 1: INFORMACIÓN BÁSICA === */}
          {step === 1 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <FileText size={20} className="text-purple-400" />
                Información Básica
              </h2>
              
              <div>
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Título del anuncio *</label>
                <input 
                  type="text" 
                  value={formData.title} 
                  onChange={(e) => setFormData({...formData, title: e.target.value})} 
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50" 
                  placeholder="Ej: Ático reformado con vistas al mar" 
                />
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Descripción detallada *</label>
                <textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({...formData, description: e.target.value})} 
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 h-32 resize-none" 
                  placeholder="Describe la propiedad con detalle: distribución, reformas, vistas, luminosidad..." 
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Tipo de propiedad</label>
                  <select value={formData.property_type} onChange={(e) => setFormData({...formData, property_type: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white">
                    <option value="apartamento">Apartamento</option>
                    <option value="piso">Piso</option>
                    <option value="casa">Casa</option>
                    <option value="chalet">Chalet</option>
                    <option value="atico">Ático</option>
                    <option value="duplex">Dúplex</option>
                    <option value="estudio">Estudio</option>
                    <option value="bungalow">Bungalow</option>
                    <option value="finca">Finca rústica</option>
                    <option value="terreno">Terreno</option>
                    <option value="local">Local comercial</option>
                    <option value="oficina">Oficina</option>
                    <option value="nave">Nave industrial</option>
                    <option value="garaje">Garaje</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Operación</label>
                  <select value={formData.listing_type} onChange={(e) => setFormData({...formData, listing_type: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white">
                    <option value="venta">Venta</option>
                    <option value="alquiler">Alquiler</option>
                    <option value="alquiler_opcion_compra">Alquiler con opción a compra</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">
                  Precio {formData.listing_type === 'venta' ? '(€)' : '(€/mes)'} *
                </label>
                <input 
                  type="number" 
                  value={formData.price} 
                  onChange={(e) => setFormData({...formData, price: e.target.value})} 
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50" 
                  placeholder={formData.listing_type === 'venta' ? '250000' : '1200'} 
                />
              </div>
              
              {formData.listing_type === 'venta' && (
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">
                    Precio de Compra (€) <span className="text-xs text-gray-500">(Opcional - Para cálculo de plusvalía)</span>
                  </label>
                  <input
                    type="number"
                    value={formData.purchase_price}
                    onChange={(e) => setFormData({...formData, purchase_price: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    placeholder="200000"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Precio al que compraste la propiedad — calcula la ganancia y plusvalía municipal
                  </p>
                  {/* Preview en tiempo real */}
                  <PlusvaliaPreview
                    purchasePrice={formData.purchase_price}
                    salePrice={formData.price}
                  />
                </div>
              )}
              
              <button 
                onClick={() => setStep(2)} 
                disabled={!formData.title || !formData.price}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-medium hover:opacity-90 transition-all disabled:opacity-50`}
              >
                Siguiente <ChevronRight size={18} />
              </button>
            </div>
          )}
          
          {/* === PASO 2: UBICACIÓN === */}
          {step === 2 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <MapPin size={20} className="text-purple-400" />
                Ubicación
              </h2>
              
              <div>
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Provincia *</label>
                <select value={formData.province} onChange={(e) => handleProvinceChange(e.target.value)} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white">
                  {PROVINCE_LIST.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Localidad / Ciudad *</label>
                <input 
                  type="text" 
                  value={formData.city} 
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                  placeholder="Ej: Mojácar, Madrid, Barcelona..." 
                />
              </div>
              
              <div className="relative" ref={suggestionsRef}>
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block flex items-center gap-2">
                  Nombre de la calle *
                  {addressVerified && <CheckCircle size={14} className="text-green-500" />}
                </label>
                <div className="flex gap-2">
                  <input 
                    type="text" 
                    value={formData.street_name} 
                    onChange={(e) => {
                      setFormData({...formData, street_name: e.target.value});
                      setAddressVerified(false);
                    }}
                    className={`flex-1 bg-gray-800 border rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50 ${
                      addressVerified ? 'border-green-500/50' : 'border-white/10'
                    }`}
                    placeholder="Ej: Calle Gran Vía" 
                  />
                  <button 
                    onClick={verifyCurrentAddress}
                    disabled={addressSearching || !formData.street_name}
                    className={`px-4 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-medium hover:opacity-90 transition-all disabled:opacity-50`}
                  >
                    {addressSearching ? <Loader size={18} className="animate-spin" /> : <Search size={18} />}
                  </button>
                </div>
                
                {showSuggestions && addressSuggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-2 bg-gray-800 border border-white/20 rounded-xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto">
                    {addressSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectAddress(suggestion)}
                        className="w-full px-4 py-3 text-left hover:bg-purple-500/20 transition-colors border-b border-white/5 last:border-0"
                      >
                        <div className="flex items-start gap-2">
                          <MapPin size={16} className="text-purple-400 mt-0.5 flex-shrink-0" />
                          <span className="text-sm text-white">{suggestion.display_name}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Número *</label>
                  <input 
                    type="text" 
                    value={formData.street_number} 
                    onChange={(e) => setFormData({...formData, street_number: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none" 
                    placeholder="Ej: 45" 
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Código Postal</label>
                  <input 
                    type="text" 
                    value={formData.postal_code} 
                    onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                    className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none" 
                    placeholder="28001" 
                  />
                </div>
              </div>
              
              {addressVerified && (
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
                  <div className="flex items-start gap-2">
                    <CheckCircle size={16} className="text-green-500 mt-0.5" />
                    <div>
                      <p className="text-xs text-green-400 font-medium">Calle verificada</p>
                      <p className="text-xs text-gray-400 mt-1">📍 Ubicación aproximada por seguridad</p>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:bg-white/5">
                  <ChevronLeft size={18} className="inline mr-1" /> Atrás
                </button>
                <button 
                  onClick={() => setStep(3)} 
                  disabled={!addressVerified || !formData.street_number}
                  className={`flex-1 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-medium hover:opacity-90 disabled:opacity-50`}
                >
                  Siguiente <ChevronRight size={18} className="inline ml-1" />
                </button>
              </div>
            </div>
          )}
          
          {/* === PASO 3: CARACTERÍSTICAS === */}
          {step === 3 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <Building size={20} className="text-purple-400" />
                Características
              </h2>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Habitaciones *</label>
                  <input type="number" min="0" value={formData.bedrooms} onChange={(e) => setFormData({...formData, bedrooms: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Baños *</label>
                  <input type="number" min="0" value={formData.bathrooms} onChange={(e) => setFormData({...formData, bathrooms: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Superficie (m²) *</label>
                  <input type="number" min="0" value={formData.area} onChange={(e) => setFormData({...formData, area: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Parcela (m²)</label>
                  <input type="number" min="0" value={formData.plot_area} onChange={(e) => setFormData({...formData, plot_area: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" placeholder="Solo para casas/chalets" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Planta</label>
                  <input type="number" min="0" value={formData.floor} onChange={(e) => setFormData({...formData, floor: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" placeholder="0 = Bajo" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Total plantas</label>
                  <input type="number" min="1" value={formData.total_floors} onChange={(e) => setFormData({...formData, total_floors: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Año construcción</label>
                  <input type="number" min="1800" max="2026" value={formData.year_built} onChange={(e) => setFormData({...formData, year_built: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" placeholder="1990" />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Año última reforma</label>
                  <input type="number" min="1900" max="2026" value={formData.renovation_year} onChange={(e) => setFormData({...formData, renovation_year: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" placeholder="2020" />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Estado</label>
                  <select value={formData.condition} onChange={(e) => setFormData({...formData, condition: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white">
                    <option value="nuevo">Obra nueva</option>
                    <option value="reformado">Reformado</option>
                    <option value="buen_estado">Buen estado</option>
                    <option value="a_reformar">A reformar</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">
                    Certificado energético *
                    <span className="text-red-400 ml-1">(obligatorio)</span>
                  </label>
                  <select 
                    value={formData.energy_cert} 
                    onChange={(e) => setFormData({...formData, energy_cert: e.target.value})} 
                    className={`w-full bg-gray-800 border rounded-xl px-4 py-3 text-white ${
                      !formData.energy_cert ? 'border-red-500/50' : 'border-white/10'
                    }`}
                    required
                  >
                    <option value="">-- Selecciona --</option>
                    <option value="A">A (Muy eficiente)</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                    <option value="D">D</option>
                    <option value="E">E</option>
                    <option value="F">F</option>
                    <option value="G">G (Poco eficiente)</option>
                    <option value="en_tramite">En trámite</option>
                    <option value="exento">Exento</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">
                  Orientación (selecciona una o varias)
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {['Norte', 'Sur', 'Este', 'Oeste', 'Noreste', 'Noroeste', 'Sureste', 'Suroeste'].map(dir => (
                    <button
                      key={dir}
                      type="button"
                      onClick={() => {
                        const orientations = formData.orientations || [];
                        const lower = dir.toLowerCase();
                        if (orientations.includes(lower)) {
                          setFormData({...formData, orientations: orientations.filter(o => o !== lower)});
                        } else {
                          setFormData({...formData, orientations: [...orientations, lower]});
                        }
                      }}
                      className={`px-3 py-2 rounded-lg text-sm transition-all ${
                        (formData.orientations || []).includes(dir.toLowerCase())
                          ? 'bg-purple-500 text-white'
                          : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                      }`}
                    >
                      {dir}
                    </button>
                  ))}
                </div>
                {(formData.orientations || []).length > 0 && (
                  <p className="text-xs text-purple-400 mt-2">
                    Seleccionadas: {(formData.orientations || []).map(o => o.charAt(0).toUpperCase() + o.slice(1)).join(', ')}
                  </p>
                )}
              </div>
              
              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:bg-white/5">
                  <ChevronLeft size={18} className="inline mr-1" /> Atrás
                </button>
                <button 
                  onClick={() => setStep(4)} 
                  disabled={!formData.bedrooms || !formData.bathrooms || !formData.area || !formData.energy_cert}
                  className={`flex-1 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-medium hover:opacity-90 disabled:opacity-50`}
                >
                  Siguiente <ChevronRight size={18} className="inline ml-1" />
                </button>
              </div>
              
              {!formData.energy_cert && (
                <p className="text-center text-xs text-red-400">
                  El certificado energético es obligatorio
                </p>
              )}
            </div>
          )}
          
          {/* === PASO 4: EXTRAS Y DATOS FINANCIEROS === */}
          {step === 4 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <TrendingUp size={20} className="text-purple-400" />
                Extras y Datos Financieros
              </h2>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  { key: 'has_elevator', label: 'Ascensor', icon: '🛗' },
                  { key: 'has_parking', label: 'Parking', icon: '🚗' },
                  { key: 'has_pool', label: 'Piscina', icon: '🏊' },
                  { key: 'has_garden', label: 'Jardín', icon: '🌳' },
                  { key: 'has_terrace', label: 'Terraza', icon: '🌅' },
                  { key: 'has_storage', label: 'Trastero', icon: '📦' },
                  { key: 'has_ac', label: 'Aire acond.', icon: '❄️' },
                  { key: 'has_heating', label: 'Calefacción', icon: '🔥' },
                  { key: 'is_furnished', label: 'Amueblado', icon: '🛋️' },
                ].map(item => (
                  <button
                    key={item.key}
                    onClick={() => setFormData({...formData, [item.key]: !formData[item.key]})}
                    className={`p-3 rounded-xl border transition-all text-left ${
                      formData[item.key] 
                        ? 'border-purple-500 bg-purple-500/20 text-white' 
                        : 'border-white/10 text-gray-400 hover:border-white/30'
                    }`}
                  >
                    <span className="mr-2">{item.icon}</span>
                    <span className="text-sm">{item.label}</span>
                  </button>
                ))}
              </div>
              
              {formData.has_terrace && (
                <div>
                  <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Superficie terraza (m²)</label>
                  <input type="number" value={formData.terrace_area} onChange={(e) => setFormData({...formData, terrace_area: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" />
                </div>
              )}
              
              {formData.has_parking && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      id="parking_included"
                      checked={formData.parking_included} 
                      onChange={(e) => setFormData({...formData, parking_included: e.target.checked})}
                      className="w-5 h-5 rounded"
                    />
                    <label htmlFor="parking_included" className="text-sm text-gray-300">Parking incluido en precio</label>
                  </div>
                  {!formData.parking_included && (
                    <div>
                      <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Precio parking (€)</label>
                      <input type="number" value={formData.parking_price} onChange={(e) => setFormData({...formData, parking_price: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" />
                    </div>
                  )}
                </div>
              )}
              
              <div className="border-t border-white/10 pt-5 mt-5">
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <Calculator size={16} className="text-yellow-400" />
                  Datos para cálculo de rentabilidad
                  <span className="text-xs text-gray-500 font-normal">(Opcional pero recomendado)</span>
                </h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Gastos comunidad (€/mes)</label>
                    <input type="number" value={formData.community_fee} onChange={(e) => setFormData({...formData, community_fee: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" placeholder="80" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">IBI anual (€)</label>
                    <input type="number" value={formData.ibi_annual} onChange={(e) => setFormData({...formData, ibi_annual: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" placeholder="500" />
                  </div>
                </div>
                
                {formData.listing_type === 'venta' && (
                  <div className="mt-4">
                    <label className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1.5 block">Estimación alquiler mensual (€)</label>
                    <input type="number" value={formData.rental_estimate} onChange={(e) => setFormData({...formData, rental_estimate: e.target.value})} className="w-full bg-gray-800 border border-white/10 rounded-xl px-4 py-3 text-white" placeholder="1200" />
                    <p className="text-xs text-gray-500 mt-1">Para calcular el ROI potencial de la inversión</p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-3">
                <button onClick={() => setStep(3)} className="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:bg-white/5">
                  <ChevronLeft size={18} className="inline mr-1" /> Atrás
                </button>
                <button 
                  onClick={() => setStep(5)}
                  className={`flex-1 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-medium hover:opacity-90`}
                >
                  Siguiente <ChevronRight size={18} className="inline ml-1" />
                </button>
              </div>
            </div>
          )}
          
          {/* === PASO 5: IMÁGENES === */}
          {step === 5 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <Camera size={20} className="text-purple-400" />
                Imágenes de la propiedad
              </h2>
              
              <p className="text-sm text-gray-400">
                Sube fotos de calidad. Arrastra para reordenar. La primera será la imagen de portada.
              </p>
              
              {/* Upload area */}
              <div 
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-purple-500/50 transition-colors"
              >
                <Upload size={40} className="mx-auto text-gray-500 mb-3" />
                <p className="text-gray-400">Haz clic o arrastra imágenes aquí</p>
                <p className="text-xs text-gray-500 mt-1">JPG, PNG o WEBP. Máximo 10MB por imagen</p>
                <input 
                  ref={fileInputRef}
                  type="file" 
                  multiple 
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
              
              {/* Image grid with drag & drop */}
              {formData.images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {formData.images.map((img, idx) => (
                    <div 
                      key={img.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, idx)}
                      onDragOver={(e) => handleDragOver(e, idx)}
                      onDragEnd={handleDragEnd}
                      className={`relative group rounded-xl overflow-hidden border-2 ${
                        idx === 0 ? 'border-yellow-500' : 'border-white/10'
                      } ${draggedImage === idx ? 'opacity-50' : ''}`}
                    >
                      <img src={img.preview} alt={`Preview ${idx + 1}`} className="w-full aspect-square object-cover" />
                      
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                        <div className="flex justify-between items-start">
                          <div className="bg-black/50 px-2 py-1 rounded text-xs text-white flex items-center gap-1 cursor-move">
                            <GripVertical size={12} />
                            {idx + 1}
                          </div>
                          <button onClick={() => removeImage(img.id)} className="bg-red-500 p-1 rounded hover:bg-red-600">
                            <X size={14} className="text-white" />
                          </button>
                        </div>
                        
                        <select 
                          value={img.category}
                          onChange={(e) => updateImageCategory(img.id, e.target.value)}
                          className="bg-black/70 text-white text-xs rounded px-2 py-1 w-full"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {IMAGE_CATEGORIES.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.icon} {cat.label}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Portada badge */}
                      {idx === 0 && (
                        <div className="absolute top-2 left-2 bg-yellow-500 text-black text-xs font-bold px-2 py-1 rounded">
                          PORTADA
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {formData.images.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Image size={48} className="mx-auto mb-2 opacity-30" />
                  <p>No hay imágenes añadidas</p>
                </div>
              )}
              
              <div className="flex gap-3">
                <button onClick={() => setStep(4)} className="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:bg-white/5">
                  <ChevronLeft size={18} className="inline mr-1" /> Atrás
                </button>
                <button 
                  onClick={() => setStep(6)}
                  disabled={formData.images.length === 0}
                  className={`flex-1 py-3 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-medium hover:opacity-90 disabled:opacity-50`}
                >
                  Siguiente <ChevronRight size={18} className="inline ml-1" />
                </button>
              </div>
            </div>
          )}
          
          {/* === PASO 6: CONFIRMAR === */}
          {step === 6 && (
            <div className="space-y-5">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                <Check size={20} className="text-green-400" />
                Resumen y Publicar
              </h2>
              
              {/* Preview card */}
              <div className={`bg-gradient-to-r from-yellow-500 to-amber-500 rounded-xl p-5 text-black`}>
                {formData.images[0] && (
                  <img src={formData.images[0].preview} alt="Portada" className="w-full h-48 object-cover rounded-lg mb-4" />
                )}
                <h3 className="font-bold text-xl mb-1">{formData.title}</h3>
                <p className="text-sm opacity-80 mb-3">{formData.street_name} {formData.street_number}, {formData.city}</p>
                <div className="text-3xl font-bold">{parseInt(formData.price).toLocaleString('es-ES')} €{formData.listing_type !== 'venta' && '/mes'}</div>
              </div>
              
              {/* Details grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="bg-gray-800 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">Tipo</p>
                  <p className="text-white capitalize">{formData.property_type}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">Habitaciones</p>
                  <p className="text-white">{formData.bedrooms}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">Baños</p>
                  <p className="text-white">{formData.bathrooms}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">Superficie</p>
                  <p className="text-white">{formData.area} m²</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">Estado</p>
                  <p className="text-white capitalize">{formData.condition.replace('_', ' ')}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">Año</p>
                  <p className="text-white">{formData.year_built || 'N/A'}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">Cert. energético</p>
                  <p className="text-white">{formData.energy_cert || 'N/A'}</p>
                </div>
                <div className="bg-gray-800 rounded-xl p-3">
                  <p className="text-gray-500 text-xs">Imágenes</p>
                  <p className="text-white">{formData.images.length}</p>
                </div>
              </div>
              
              {/* Extras badges */}
              <div className="flex flex-wrap gap-2">
                {formData.has_elevator && <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-xs">🛗 Ascensor</span>}
                {formData.has_parking && <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-xs">🚗 Parking</span>}
                {formData.has_pool && <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-xs">🏊 Piscina</span>}
                {formData.has_terrace && <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-xs">🌅 Terraza</span>}
                {formData.has_ac && <span className="bg-purple-500/20 text-purple-300 px-3 py-1 rounded-full text-xs">❄️ A/C</span>}
              </div>
              
              {user?.subscription_plan === 'premium' && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex items-start gap-3">
                  <Crown size={20} className="text-yellow-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-semibold text-yellow-400 mb-1">Análisis Premium Incluido</div>
                    <p className="text-xs text-gray-400">Se calcularán automáticamente: ROI, plusvalía estimada, análisis de mercado y recomendación de precio.</p>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3">
                <button onClick={() => setStep(5)} className="flex-1 py-3 rounded-xl border border-white/10 text-gray-400 hover:bg-white/5">
                  <ChevronLeft size={18} className="inline mr-1" /> Atrás
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || uploadingImages}
                  className={`flex-1 py-4 rounded-xl bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2`}
                >
                  {loading || uploadingImages ? (
                    <>
                      <Loader size={20} className="animate-spin" />
                      {uploadingImages ? 'Subiendo imágenes...' : 'Publicando...'}
                    </>
                  ) : (
                    <>
                      <Check size={20} />
                      Publicar Propiedad
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
